/**
 * Account detail — view a user's memberships across orgs and revoke
 * any single membership. Lifecycle actions on the whole account
 * (deactivate / reactivate / delete) live on the list page.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cloneElement, useState, type ReactElement, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';

import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { ApiError } from '@optum/api-client/client';
import {
  deactivateAccount,
  getAccount,
  revokeMembership,
} from '@optum/api-client/endpoints/accounts';

import { useAuth } from '../../auth/AuthContext.js';
import { Button } from '../../ui/Button.js';
import { SectionCard, SectionCardBody } from '../../ui/SectionCard.js';
import { Banner } from '../../components/feedback/Banner.js';
import { EmptyBlock } from '../../ui/AsyncState.js';
import { useBreadcrumbTail } from '../../components/shell/BreadcrumbTailContext.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { Tabs, type TabItem } from '../../ui/Tabs.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDateTime } from '../../lib/format.js';
import { shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { useConfirm } from '../../ui/Confirm.js';
import { RecordShape, ShapeError, ShapeLoading } from '../../ui/shapes.js';
import { DataTable } from '../../ui/DataTable.js';
import { Avatar, KeyValue, Typography } from '../../ui/finixt.js';
import { MemberGroupsEditor } from './MemberGroupsEditor.js';
import { UserCapabilityConfigEditor } from './UserCapabilityConfigEditor.js';

type AccountTab = 'organizations' | 'groups' | 'configuration';

export function AccountDetail() {
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  const [tab, setTab] = useState<AccountTab>('organizations');
  const { externalSub = '' } = useParams<{ externalSub: string }>();
  const { isPlatformAdmin } = useAuth();
  const query = useQuery({
    queryKey: qk.account(externalSub),
    queryFn: () => getAccount(externalSub),
  });

  useBreadcrumbTail(query.data?.email);

  if (query.isPending) {
    return <ShapeLoading shape="record" />;
  }
  if (query.isError) {
    return <ShapeError shape="record" error={query.error} fallback="Failed to load" />;
  }
  const account = query.data;
  const adminMemberships = account.memberships.filter(
    (m) => m.role === 'platform-client-admin' && m.status === 'active',
  );

  const tabs: TabItem<AccountTab>[] = [
    {
      value: 'organizations',
      label: 'Organizations',
      icon: 'apartment',
      count: account.memberships.length,
    },
    ...(isPlatformAdmin
      ? ([
          { value: 'groups', label: 'Groups', icon: 'shield_person' },
          { value: 'configuration', label: 'Configuration', icon: 'settings' },
        ] as TabItem<AccountTab>[])
      : []),
  ];

  return (
    <RecordShape
      back={{ to: '/accounts', label: 'Accounts' }}
      avatar={<Avatar initials={account.email.slice(0, 1).toUpperCase()} size={48} />}
      eyebrow={
        <>
          <Typography variant="eyebrow">Account</Typography>
          <StatusBadge status={account.status} />
        </>
      }
      title={account.email}
      subtitle={
        <Typography variant="meta" mono as="span">
          {`${account.idp_name} · ${account.external_sub}`}
        </Typography>
      }
      action={
        isPlatformAdmin && account.status === 'active' ? (
          <DeactivateButton externalSub={account.external_sub} email={account.email} />
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 30, paddingBottom: 64 }}>
        {adminMemberships.length > 0 && (
          <Banner title="This account holds admin roles">
            Active <code>client-admin</code> in{' '}
            <strong>
              {adminMemberships.length} organization
              {adminMemberships.length === 1 ? '' : 's'}
            </strong>
            : {adminMemberships.map((m) => m.organization_name).join(', ')}. Deactivating or
            revoking the account will be refused if doing so leaves any of those orgs without an
            active admin — promote a co-member or add a new admin first.
          </Banner>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) minmax(0,320px)',
            alignItems: 'flex-start',
            gap: 22,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Tabs items={tabs} value={tab} onChange={setTab} />

            {tab === 'organizations' && (
              <SectionCard
                title="Organizations"
                icon="apartment"
                subtitle="Memberships for this account."
              >
                {account.memberships.length === 0 ? (
                  <SectionCardBody>
                    <EmptyBlock>This account has no memberships.</EmptyBlock>
                  </SectionCardBody>
                ) : (
                  <DataTable
                    rows={account.memberships}
                    rowKey="organization_id"
                    onRowClick={(m) => panel.open({ kind: 'organization', id: m.organization_id })}
                    onRowOpenPage={(m) => openPage({ kind: 'organization', id: m.organization_id })}
                    columns={[
                      {
                        key: 'organization_name',
                        label: 'Organization',
                        strong: true,
                        wrap: true,
                        render: (m) => (
                          <>
                            <div style={{ fontWeight: 'var(--weight-medium)' }}>
                              {m.organization_name}
                            </div>
                            <div
                              style={{
                                fontSize: 'var(--text-meta-lg)',
                                color: 'var(--text-muted)',
                              }}
                              className="mono"
                            >
                              {shortId(m.organization_id)}
                            </div>
                          </>
                        ),
                      },
                      { key: 'role', label: 'Role', mono: true },
                      {
                        key: 'status',
                        label: 'Status',
                        render: (m) => <StatusBadge status={m.status} />,
                      },
                      {
                        key: 'actions',
                        label: 'Actions',
                        align: 'right',
                        stopRowClick: true,
                        render: (m) =>
                          isPlatformAdmin ? (
                            <RevokeMembership
                              externalSub={account.external_sub}
                              orgId={m.organization_id}
                              orgName={m.organization_name}
                            />
                          ) : (
                            <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>
                              —
                            </span>
                          ),
                      },
                    ]}
                  />
                )}
              </SectionCard>
            )}

            {isPlatformAdmin && tab === 'groups' && (
              <SectionCard
                title="Groups"
                icon="shield_person"
                subtitle="Groups grant this account access to ingested data. Saving replaces the whole set for the selected organization."
              >
                <SectionCardBody>
                  <MemberGroupsEditor
                    platformUserId={account.platform_user_id}
                    memberships={account.memberships}
                  />
                </SectionCardBody>
              </SectionCard>
            )}

            {isPlatformAdmin && tab === 'configuration' && (
              <SectionCard
                title="Agent configuration"
                icon="settings"
                subtitle="Per-user capability overrides for tasks this user submits. Unset fields inherit the organization and global values."
              >
                <SectionCardBody>
                  <UserCapabilityConfigEditor
                    platformUserId={account.platform_user_id}
                    memberships={account.memberships}
                  />
                </SectionCardBody>
              </SectionCard>
            )}
          </div>

          <aside>
            <MetadataCard account={account} />
          </aside>
        </div>
      </div>
    </RecordShape>
  );
}

function MetadataCard({ account }: { account: import('@optum/api-client/types').AccountDetail }) {
  return (
    <SectionCard title="Metadata" icon="info">
      <SectionCardBody>
        <KeyValue
          items={[
            { label: 'platform_user_id', value: account.platform_user_id },
            { label: 'user row status', value: account.user_status, mono: false },
            { label: 'Created', value: fmtTs(account.created_at) },
            { label: 'Updated', value: fmtTs(account.updated_at) },
            { label: 'Last login', value: fmtTs(account.last_login_at) },
          ]}
        />
      </SectionCardBody>
    </SectionCard>
  );
}

function fmtTs(value: string | null | undefined): string {
  if (!value) return '—';
  return absoluteDateTime(value);
}

function DeactivateButton({ externalSub, email }: { externalSub: string; email: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const mut = useMutation({
    mutationFn: () => deactivateAccount(externalSub),
    onSuccess: () => {
      toast.success(`Deactivated ${email}`);
      qc.invalidateQueries({ queryKey: qk.account(externalSub) });
      qc.invalidateQueries({ queryKey: qk.accounts() });
    },
    onError: (e) => toast.error('Deactivate failed', e instanceof ApiError ? e.message : String(e)),
  });
  return (
    <ConfirmAction
      trigger={
        <Button size="sm" variant="danger" disabled={mut.isPending}>
          {mut.isPending ? 'Deactivating…' : 'Deactivate'}
        </Button>
      }
      title="Deactivate this account?"
      description={
        <>
          <strong>{email}</strong> will lose access to every organization. Live sessions are revoked
          at the IdP. This is reversible via Reactivate from the list page.
        </>
      }
      confirmLabel="Deactivate"
      onConfirm={() => mut.mutate()}
    />
  );
}

function RevokeMembership({
  externalSub,
  orgId,
  orgName,
}: {
  externalSub: string;
  orgId: string;
  orgName: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const revoke = useMutation({
    mutationFn: () => revokeMembership(externalSub, orgId),
    onSuccess: () => {
      toast.success(`Revoked from ${orgName}`);
      qc.invalidateQueries({ queryKey: qk.account(externalSub) });
      qc.invalidateQueries({ queryKey: qk.accounts() });
    },
    onError: (e) => toast.error('Revoke failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <ConfirmAction
      trigger={
        <Button size="sm" variant="danger" disabled={revoke.isPending}>
          {revoke.isPending ? 'Revoking…' : 'Revoke'}
        </Button>
      }
      title={`Revoke membership in ${orgName}?`}
      description={
        <>
          This account will lose access to <strong>{orgName}</strong>. The user can be re-added
          through invitation or JIT later.
        </>
      }
      confirmLabel="Revoke"
      onConfirm={() => revoke.mutate()}
    />
  );
}

interface ConfirmActionProps {
  trigger: ReactElement<{ onClick?: (() => void) | undefined }>;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
}

function ConfirmAction({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
}: ConfirmActionProps) {
  const confirm = useConfirm();
  return cloneElement(trigger, {
    onClick: () => {
      void confirm({ title, description, confirmLabel, tone: 'danger' }).then((ok) => {
        if (ok) onConfirm();
      });
    },
  });
}
